import { useState, useEffect, useCallback, useRef } from 'react';
import './VirtualTryOn.css';
import bwSvgRaw from './assets/try-on-bw.svg?raw';
import colorSvgRaw from './assets/try-on-color-.svg?raw';

interface VirtualTryOnProps {
  apiBaseUrl: string;
}

type Phase = 'reset' | 'bw-in' | 'paired' | 'color-in' | 'cycling' | 'out';

interface Look {
  name: string;
  shirt: string;
}

// Three muted, pastel-adjacent shirt colors that harmonize with the
// salmon-coral page bg behind this scene. The colored SVG's shirt path
// (originally fill="#5f33c1") is rebound to var(--vto-shirt-color) via CSS,
// and CSS handles the smooth fill transition between looks.
const LOOKS: Look[] = [
  { name: 'Daytime', shirt: '#7A6BB5' },
  { name: 'Evening', shirt: '#B07585' },
  { name: 'Weekend', shirt: '#7B9876' },
];

// Strip the XML/DOCTYPE preamble so the imported SVG can be cleanly injected
// via dangerouslySetInnerHTML without a stray processing instruction.
function stripXmlDecl(svg: string): string {
  return svg
    .replace(/<\?xml[^?]*\?>/g, '')
    .replace(/<!DOCTYPE[^>]*>/g, '')
    .trim();
}

const BW_SVG = stripXmlDecl(bwSvgRaw);
const COLOR_SVG = stripXmlDecl(colorSvgRaw);

export function VirtualTryOn(_props: VirtualTryOnProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [phase, setPhase] = useState<Phase>('reset');
  const [lookIndex, setLookIndex] = useState(0);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  // Phase machine for one full cycle (~13s, matches cycleMs in ReelLoop):
  //   reset (300ms) -> both slots hidden, lookIndex=0
  //   bw-in (800ms) -> left B&W svg fades+slides in
  //   paired (1500ms) -> hold B&W solo so the eye reads the "before"
  //   color-in (800ms) -> right svg fades+scales in at look 0 (Daytime)
  //   cycling (3 x 3000ms) -> hold look 0, then transition+hold look 1,
  //                          then transition+hold look 2. The shirt fill
  //                          transitions over 800ms via CSS, leaving 2200ms
  //                          of hold per look.
  //   out (800ms + 200ms gap) -> whole stage fades out, then loop.
  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setPhase('reset');
      setLookIndex(0);
      await sleep(300);
      if (!loopRef.current) return;

      setPhase('bw-in');
      await sleep(800);
      if (!loopRef.current) return;

      setPhase('paired');
      await sleep(1500);
      if (!loopRef.current) return;

      setPhase('color-in');
      await sleep(800);
      if (!loopRef.current) return;

      setPhase('cycling');
      await sleep(3000);
      if (!loopRef.current) return;

      setLookIndex(1);
      await sleep(3000);
      if (!loopRef.current) return;

      setLookIndex(2);
      await sleep(3000);
      if (!loopRef.current) return;

      setPhase('out');
      await sleep(800);
      await sleep(200);
    }
  }, [sleep]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [runLoop]);

  const showBw = phase !== 'reset' && phase !== 'out';
  const showColor = phase === 'color-in' || phase === 'cycling';

  return (
    <div className="virtual-try-on">
      <div className={`vto-stage${phase === 'out' ? ' vto-fade-out' : ''}`}>
        <div className="vto-section-label sc-section-label">Virtual Try-On</div>

        <div
          className="vto-canvas sc-glass-card"
          style={{ ['--vto-shirt-color' as string]: LOOKS[lookIndex].shirt } as React.CSSProperties}
        >
          <div className="vto-pair">
            <div
              className={`vto-icon-slot vto-icon-bw${showBw ? ' vto-icon-in' : ''}`}
              dangerouslySetInnerHTML={{ __html: BW_SVG }}
              aria-hidden="true"
            />
            <div
              className={`vto-icon-slot vto-icon-color${showColor ? ' vto-icon-in' : ''}`}
              dangerouslySetInnerHTML={{ __html: COLOR_SVG }}
              aria-hidden="true"
            />
          </div>

          <div className="vto-footer">
            <div className="vto-look-name">{LOOKS[lookIndex].name}</div>
            <div className="vto-dots">
              {LOOKS.map((_, i) => (
                <span
                  key={i}
                  className={`vto-dot${i === lookIndex ? ' vto-dot-active' : ''}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
